import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Briefcase, 
  Users, 
  Clock, 
  DollarSign,
  TrendingUp,
  ArrowRight,
  FileText,
  Download,
  CheckCircle
} from 'lucide-react';

const DashboardPage = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      review: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      on_hold: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900">
          Welcome back, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-slate-500 mt-1">Here's what's happening with your projects</p>
      </div>

      {/* Stats Grid - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/jobs">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="stat-active-jobs">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Active Jobs</p>
                  <p className="text-3xl font-heading font-bold text-[#1F3C4F] mt-2">
                    {stats?.active_jobs || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    of {stats?.total_jobs || 0} total
                  </p>
                </div>
                <div className="p-3 bg-[#1F3C4F]/10 rounded-xl">
                  <Briefcase className="w-6 h-6 text-[#1F3C4F]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/customers">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="stat-customers">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Customers</p>
                  <p className="text-3xl font-heading font-bold text-[#1F3C4F] mt-2">
                    {stats?.total_customers || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Active clients
                  </p>
                </div>
                <div className="p-3 bg-[#1F3C4F]/10 rounded-xl">
                  <Users className="w-6 h-6 text-[#1F3C4F]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/time-tracking">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="stat-hours">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Hours</p>
                  <p className="text-3xl font-heading font-bold text-[#FF1493] mt-2">
                    {stats?.total_hours?.toFixed(1) || '0.0'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Hours logged
                  </p>
                </div>
                <div className="p-3 bg-[#FF1493]/10 rounded-xl">
                  <Clock className="w-6 h-6 text-[#FF1493]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/expenses">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="stat-expenses">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Expenses</p>
                  <p className="text-3xl font-heading font-bold text-[#FFB800] mt-2">
                    {formatCurrency(stats?.total_expenses)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    All time
                  </p>
                </div>
                <div className="p-3 bg-[#FFB800]/10 rounded-xl">
                  <DollarSign className="w-6 h-6 text-[#FFB800]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Jobs */}
      <Card className="border-0 shadow-sm" data-testid="recent-jobs-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-lg">Recent Jobs</CardTitle>
          <Link 
            to="/jobs" 
            className="text-sm text-[#1F3C4F] hover:underline flex items-center gap-1"
          >
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.recent_jobs?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_jobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-[#1F3C4F]/20 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1F3C4F]/10 flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-[#1F3C4F]" />
                    </div>
                    <div>
                      <p className="font-mono text-sm text-slate-500">{job.job_number}</p>
                      <p className="font-medium text-slate-900">{job.title}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={getStatusBadge(job.status)}>
                      {job.status?.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm text-slate-500">{job.job_type}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p>No jobs yet. Create your first job to get started!</p>
              <Link 
                to="/jobs" 
                className="inline-block mt-4 text-[#1F3C4F] hover:underline"
              >
                Create a Job
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="quick-actions">
        <Link to="/jobs" className="block h-full">
          <Card className="border-0 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer bg-gradient-to-br from-[#1F3C4F] to-[#152a38] h-full" data-testid="quick-action-job">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 text-white">
                <Briefcase className="w-8 h-8 flex-shrink-0" />
                <div>
                  <p className="font-heading font-bold text-base">Create New Job</p>
                  <p className="text-white/70 text-sm">Start tracking a new project</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/time-tracking" className="block h-full">
          <Card className="border-0 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] h-full" data-testid="quick-action-time">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 text-white">
                <Clock className="w-8 h-8 flex-shrink-0" />
                <div>
                  <p className="font-heading font-bold text-base">Log Time</p>
                  <p className="text-white/70 text-sm">Record work hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/expenses" className="block h-full">
          <Card className="border-0 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer bg-gradient-to-br from-[#FF1493] to-[#CC0F75] h-full" data-testid="quick-action-expense">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 text-white">
                <DollarSign className="w-8 h-8 flex-shrink-0" />
                <div>
                  <p className="font-heading font-bold text-base">Add Expense</p>
                  <p className="text-white/70 text-sm">Track business costs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Workflow Guide */}
      <Card className="border-0 shadow-sm bg-slate-50" data-testid="workflow-guide">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#1F3C4F]" />
            Getting Started - Site Workflow
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link to="/customers" className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-100 hover:border-[#1F3C4F]/20 hover:shadow-sm transition-all">
              <div className="w-8 h-8 rounded-full bg-[#1F3C4F] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-slate-900">Add Customers</p>
                <p className="text-sm text-slate-500">Add clients to CRM</p>
              </div>
            </Link>
            
            <Link to="/jobs" className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-100 hover:border-[#1F3C4F]/20 hover:shadow-sm transition-all">
              <div className="w-8 h-8 rounded-full bg-[#1F3C4F] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-slate-900">Create Jobs</p>
                <p className="text-sm text-slate-500">Create projects for customers</p>
              </div>
            </Link>
            
            <Link to="/time-tracking" className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-100 hover:border-[#1F3C4F]/20 hover:shadow-sm transition-all">
              <div className="w-8 h-8 rounded-full bg-[#1F3C4F] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-slate-900">Track Time</p>
                <p className="text-sm text-slate-500">Log hours on jobs</p>
              </div>
            </Link>
            
            <Link to="/expenses" className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-100 hover:border-[#FF1493]/20 hover:shadow-sm transition-all">
              <div className="w-8 h-8 rounded-full bg-[#FF1493] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
              <div>
                <p className="font-medium text-slate-900">Track Expenses</p>
                <p className="text-sm text-slate-500">Mileage, meals, travel costs</p>
              </div>
            </Link>
            
            <Link to="/invoices" className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-100 hover:border-[#FFB800]/20 hover:shadow-sm transition-all">
              <div className="w-8 h-8 rounded-full bg-[#FFB800] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">5</div>
              <div>
                <p className="font-medium text-slate-900">Create Invoices</p>
                <p className="text-sm text-slate-500">Bill customers for work</p>
              </div>
            </Link>
            
            <Link to="/expenses" className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-100 hover:border-green-500/20 hover:shadow-sm transition-all">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">6</div>
              <div>
                <p className="font-medium text-slate-900">Export Reports</p>
                <p className="text-sm text-slate-500">CSV, Excel, PDF exports</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
